import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { createCashflowItemsBulk } from "@/api/cashflow";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatRuDate } from "@/lib/format";
import { SELECT_CLASSES } from "@/lib/styles";
import {
  CategoryField,
  ErrorPanel,
  FieldError,
  FirmnessField,
  OrgField,
} from "@/pages/cashflow/ManualFields";
import { errorDetail, errorStatus } from "@/pages/cashflow/utils";
import type { CashflowCategory, CashflowOrg } from "@/types/api";

const schema = z.object({
  title: z.string().trim().min(1, "Назовите платежи").max(200),
  org_inn: z.string().min(1, "Выберите организацию"),
  category: z.string().min(1, "Выберите статью"),
  firmness: z.enum(["committed", "planned", "assumption"]),
  text: z.string().min(1, "Вставьте строки «дд.мм.гггг;сумма»"),
  comment: z.string(),
  allow_duplicates: z.boolean(),
});

type Values = z.infer<typeof schema>;

interface Props {
  orgInns: string[];
  orgs: CashflowOrg[];
  categories: CashflowCategory[];
  /**
   * Дата расчёта календаря (с бэка) — с неё же бэк отсчитывает «не в
   * прошлом»: сравнивает с московским «сегодня», а не с датой браузера.
   */
  asOf: string;
  onCreated: () => void;
  onCancel: () => void;
}

export function ManualBulkForm({
  orgInns,
  orgs,
  categories,
  asOf,
  onCreated,
  onCancel,
}: Props): React.ReactElement {
  // Ответ сервера показываем текстом рядом с формой: «строка 7: …» или список
  // дублей — по тосту такое не разберёшь.
  const [serverError, setServerError] = useState<{ status: number | null; detail: string } | null>(
    null,
  );
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: "",
      org_inn: orgInns[0] ?? "",
      category: "",
      firmness: "planned",
      text: "",
      comment: "",
      allow_duplicates: false,
    },
  });

  const mut = useMutation({
    mutationFn: (v: Values) =>
      createCashflowItemsBulk({
        text: v.text,
        title: v.title.trim(),
        org_inn: v.org_inn,
        category: v.category,
        firmness: v.firmness,
        comment: v.comment.trim() === "" ? null : v.comment.trim(),
        allow_duplicates: v.allow_duplicates,
      }),
    onSuccess: (res) => {
      setServerError(null);
      toast.success(`Добавлено строк: ${res.created}`);
      reset();
      onCreated();
    },
    onError: (err: unknown) => {
      setServerError({ status: errorStatus(err), detail: errorDetail(err) });
    },
  });

  return (
    <form
      className="space-y-4 rounded-md border p-4"
      onSubmit={handleSubmit((v) => {
        setServerError(null);
        mut.mutate(v);
      })}
    >
      <div className="grid gap-3 md:grid-cols-3">
        <div className="space-y-1.5 md:col-span-2">
          <Label htmlFor="bulk_title">Название (одно на все строки)</Label>
          <Input id="bulk_title" placeholder="напр. Кредит Сбер, платёж" {...register("title")} />
          <FieldError message={errors.title?.message} />
        </div>
        <OrgField
          id="bulk_org"
          field={register("org_inn")}
          error={errors.org_inn?.message}
          orgInns={orgInns}
          orgs={orgs}
        />
        <CategoryField
          id="bulk_category"
          field={register("category")}
          error={errors.category?.message}
          categories={categories}
        />
        <FirmnessField
          id="bulk_firmness"
          field={register("firmness")}
          error={errors.firmness?.message}
        />
        <div className="space-y-1.5 md:col-span-2">
          <Label htmlFor="bulk_comment">Комментарий</Label>
          <Input id="bulk_comment" {...register("comment")} />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="bulk_text">Строки «дд.мм.гггг;сумма» — по одной в строке</Label>
        <textarea
          id="bulk_text"
          rows={8}
          className={`${SELECT_CLASSES} h-auto font-mono`}
          placeholder={"13.10.2026;666849.32\n13.11.2026;670924.66"}
          {...register("text")}
        />
        <FieldError message={errors.text?.message} />
        {/* Границу называем датой, а не «сегодня»: бэк сверяет с московским
            «сегодня», и оно может не совпасть с датой в браузере. */}
        <p className="text-xs text-muted-foreground">
          Даты раньше {formatRuDate(asOf)} не принимаются: прошлые платежи уже в
          остатке календаря.
        </p>
      </div>

      <div className="flex items-center gap-2">
        <input
          id="bulk_allow_duplicates"
          type="checkbox"
          className="h-4 w-4"
          {...register("allow_duplicates")}
        />
        <Label htmlFor="bulk_allow_duplicates" className="font-normal">
          Разрешить дубли (это действительно новые платежи)
        </Label>
      </div>

      {serverError && (
        <ErrorPanel detail={serverError.detail}>
          {serverError.status === 409 && (
            <p className="text-xs">
              Если это правда новые платежи — поставьте галочку «Разрешить дубли» и
              нажмите «Вставить» ещё раз.
            </p>
          )}
        </ErrorPanel>
      )}

      <div className="flex gap-2">
        <Button type="submit" disabled={mut.isPending}>
          {mut.isPending ? "Вставка…" : "Вставить"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Отмена
        </Button>
      </div>
    </form>
  );
}
